import { create } from "zustand";
import { fetchApi } from "@/lib/api";
import { captureException } from "@/lib/analytics";
import {
  Invoice,
  InvoiceStatus,
  CreateInvoiceParams,
  UpdateInvoiceParams,
  ListInvoicesResponseSchema,
  CreateInvoiceResponseSchema,
  UpdateInvoiceResponseSchema,
} from "@forgepoint/shared";

interface InvoiceLoadingState {
  isLoadingGet: boolean;
  isLoadingSave: boolean;
  isLoadingDelete: boolean;
  getError: string | null;
  saveError: string | null;
  deleteError: string | null;
}

interface InvoicesStore {
  invoices: Map<string, Invoice>;
  invoiceLoadingStates: Map<string, InvoiceLoadingState>;
  listLoading: boolean;
  listError: string | null;

  fetchInvoices: (
    search?: string,
    status?: InvoiceStatus,
    salesOrderId?: string,
  ) => Promise<string[]>;
  getInvoice: (
    invoiceId?: string,
    invoiceNumber?: number,
  ) => Promise<Invoice | null>;
  createInvoice: (data: CreateInvoiceParams) => Promise<Invoice | null>;
  updateInvoice: (
    id: string,
    data: UpdateInvoiceParams,
  ) => Promise<Invoice | null>;
  updateInvoiceStatus: (
    id: string,
    status: InvoiceStatus,
  ) => Promise<Invoice | null>;
  deleteInvoice: (id: string) => Promise<boolean>;
}

const getInitialLoadingState = (): InvoiceLoadingState => ({
  isLoadingGet: false,
  isLoadingSave: false,
  isLoadingDelete: false,
  getError: null,
  saveError: null,
  deleteError: null,
});

export const useInvoicesStore = create<InvoicesStore>((set) => ({
  invoices: new Map(),
  invoiceLoadingStates: new Map(),
  listLoading: false,
  listError: null,

  fetchInvoices: async (
    search?: string,
    status?: InvoiceStatus,
    salesOrderId?: string,
  ) => {
    set({ listLoading: true, listError: null });

    try {
      const queryParams = new URLSearchParams();
      if (search) {
        queryParams.append("search", search);
      }
      if (status) {
        queryParams.append("status", status);
      }
      if (salesOrderId) {
        queryParams.append("salesOrderId", salesOrderId);
      }
      const queryString = queryParams.toString();
      const url = queryString ? `invoices?${queryString}` : "invoices";

      const response = await fetchApi(url, "GET");

      if (response.ok && response.data) {
        // Parse dates from strings before validation
        const data = ListInvoicesResponseSchema.parse(response.data);
        set((state) => {
          const invoicesMap = new Map(state.invoices);
          data.invoices.forEach((invoice) => {
            invoicesMap.set(invoice.id, invoice);
          });
          return { invoices: invoicesMap, listLoading: false };
        });
        return data.invoices.map((invoice) => invoice.id);
      }

      set({ listError: "Failed to fetch invoices", listLoading: false });
      return [];
    } catch (error) {
      captureException(error as Error);
      set({ listError: "Failed to fetch invoices", listLoading: false });
      return [];
    }
  },

  getInvoice: async (
    invoiceId?: string,
    invoiceNumber?: number,
  ): Promise<Invoice | null> => {
    if (!invoiceId && !invoiceNumber) {
      return null;
    }

    try {
      const queryParams = new URLSearchParams();
      if (invoiceId) {
        queryParams.append("ids", invoiceId);
      }
      if (invoiceNumber) {
        queryParams.append("invoiceNumbers", invoiceNumber.toString());
      }

      const response = await fetchApi(
        `invoices?${queryParams.toString()}`,
        "GET",
      );

      if (response.ok && response.data) {
        const data = ListInvoicesResponseSchema.parse(response.data);
        if (data.invoices.length === 0) {
          return null;
        }

        const invoice = data.invoices[0];
        set((state) => {
          const invoices = new Map(state.invoices);
          const loadingStates = new Map(state.invoiceLoadingStates);
          const currentState =
            loadingStates.get(invoice.id) || getInitialLoadingState();
          invoices.set(invoice.id, invoice);
          loadingStates.set(invoice.id, {
            ...currentState,
            isLoadingGet: false,
            getError: null,
          });
          return { invoices, invoiceLoadingStates: loadingStates };
        });

        return invoice;
      }

      return null;
    } catch (error) {
      captureException(error as Error);
      return null;
    }
  },

  createInvoice: async (data: CreateInvoiceParams) => {
    try {
      const response = await fetchApi("invoices", "POST", {
        body: data,
      });

      if (response.ok && response.data) {
        // Parse dates from strings before validation
        const result = CreateInvoiceResponseSchema.parse(response.data);
        set((state) => {
          const invoices = new Map(state.invoices);
          invoices.set(result.invoice.id, result.invoice);
          return { invoices };
        });
        return result.invoice;
      }

      return null;
    } catch (error) {
      captureException(error as Error);
      return null;
    }
  },

  updateInvoice: async (id: string, data: UpdateInvoiceParams) => {
    set((state) => {
      const loadingStates = new Map(state.invoiceLoadingStates);
      const currentState = loadingStates.get(id) || getInitialLoadingState();
      loadingStates.set(id, {
        ...currentState,
        isLoadingSave: true,
        saveError: null,
      });
      return { invoiceLoadingStates: loadingStates };
    });

    try {
      const response = await fetchApi(`invoices/${id}`, "PUT", {
        body: data,
      });

      if (response.ok && response.data) {
        const result = UpdateInvoiceResponseSchema.parse(response.data);
        set((state) => {
          const invoices = new Map(state.invoices);
          const loadingStates = new Map(state.invoiceLoadingStates);
          const currentState =
            loadingStates.get(id) || getInitialLoadingState();
          invoices.set(id, result.invoice);
          loadingStates.set(id, {
            ...currentState,
            isLoadingSave: false,
            saveError: null,
          });
          return { invoices, invoiceLoadingStates: loadingStates };
        });
        return result.invoice;
      }

      set((state) => {
        const loadingStates = new Map(state.invoiceLoadingStates);
        const currentState = loadingStates.get(id) || getInitialLoadingState();
        loadingStates.set(id, {
          ...currentState,
          isLoadingSave: false,
          saveError: "Failed to update invoice",
        });
        return { invoiceLoadingStates: loadingStates };
      });
      return null;
    } catch (error) {
      captureException(error as Error);
      set((state) => {
        const loadingStates = new Map(state.invoiceLoadingStates);
        const currentState = loadingStates.get(id) || getInitialLoadingState();
        loadingStates.set(id, {
          ...currentState,
          isLoadingSave: false,
          saveError: "Failed to update invoice",
        });
        return { invoiceLoadingStates: loadingStates };
      });
      return null;
    }
  },

  updateInvoiceStatus: async (id: string, status: InvoiceStatus) => {
    set((state) => {
      const loadingStates = new Map(state.invoiceLoadingStates);
      const currentState = loadingStates.get(id) || getInitialLoadingState();
      loadingStates.set(id, {
        ...currentState,
        isLoadingSave: true,
        saveError: null,
      });
      return { invoiceLoadingStates: loadingStates };
    });

    try {
      const response = await fetchApi(`invoices/${id}/status`, "PATCH", {
        body: { status },
      });

      if (response.ok && response.data) {
        const result = UpdateInvoiceResponseSchema.parse(response.data);
        set((state) => {
          const invoices = new Map(state.invoices);
          const loadingStates = new Map(state.invoiceLoadingStates);
          const currentState =
            loadingStates.get(id) || getInitialLoadingState();
          invoices.set(id, result.invoice);
          loadingStates.set(id, {
            ...currentState,
            isLoadingSave: false,
            saveError: null,
          });
          return { invoices, invoiceLoadingStates: loadingStates };
        });
        return result.invoice;
      }

      set((state) => {
        const loadingStates = new Map(state.invoiceLoadingStates);
        const currentState = loadingStates.get(id) || getInitialLoadingState();
        loadingStates.set(id, {
          ...currentState,
          isLoadingSave: false,
          saveError: "Failed to update invoice status",
        });
        return { invoiceLoadingStates: loadingStates };
      });
      return null;
    } catch (error) {
      captureException(error as Error);
      set((state) => {
        const loadingStates = new Map(state.invoiceLoadingStates);
        const currentState = loadingStates.get(id) || getInitialLoadingState();
        loadingStates.set(id, {
          ...currentState,
          isLoadingSave: false,
          saveError: "Failed to update invoice status",
        });
        return { invoiceLoadingStates: loadingStates };
      });
      return null;
    }
  },

  deleteInvoice: async (id: string) => {
    set((state) => {
      const loadingStates = new Map(state.invoiceLoadingStates);
      const currentState = loadingStates.get(id) || getInitialLoadingState();
      loadingStates.set(id, {
        ...currentState,
        isLoadingDelete: true,
        deleteError: null,
      });
      return { invoiceLoadingStates: loadingStates };
    });

    try {
      const response = await fetchApi(`invoices/${id}`, "DELETE");

      if (response.ok) {
        set((state) => {
          const invoices = new Map(state.invoices);
          const loadingStates = new Map(state.invoiceLoadingStates);
          invoices.delete(id);
          loadingStates.delete(id);
          return { invoices, invoiceLoadingStates: loadingStates };
        });
        return true;
      }

      set((state) => {
        const loadingStates = new Map(state.invoiceLoadingStates);
        const currentState = loadingStates.get(id) || getInitialLoadingState();
        loadingStates.set(id, {
          ...currentState,
          isLoadingDelete: false,
          deleteError: "Failed to delete invoice",
        });
        return { invoiceLoadingStates: loadingStates };
      });
      return false;
    } catch (error) {
      captureException(error as Error);
      set((state) => {
        const loadingStates = new Map(state.invoiceLoadingStates);
        const currentState = loadingStates.get(id) || getInitialLoadingState();
        loadingStates.set(id, {
          ...currentState,
          isLoadingDelete: false,
          deleteError: "Failed to delete invoice",
        });
        return { invoiceLoadingStates: loadingStates };
      });
      return false;
    }
  },
}));
